/* ============================================================================
   Panastore — Free Gift With Purchase  (Primo 65 Cap)
   ----------------------------------------------------------------------------
   Auto-adds a £0 "exclusive" gift variant to the cart when the merchandise
   subtotal reaches the threshold, removes it when the cart drops below, locks
   the gift to qty 1, and drives the cart-drawer progress bar.

   • Loaded ONLY when settings.free_gift_enable is true (see theme.liquid).
   • Hooks Dawn's public pub/sub API (subscribe + PUB_SUB_EVENTS.cartUpdate).
     It is a LISTENER — it never patches Dawn's own cart code.
   • Threshold is measured on cart.items_subtotal_price (merchandise subtotal,
     before cart-level discount codes) so a code can't strip the gift.
   • Re-entrancy guard (busy) + debounce prevent any add/remove loops.
   ============================================================================ */
(function () {
  'use strict';

  var cfg = window.PANASTORE_GIFT;
  if (!cfg || !cfg.variantId) return;

  var GIFT_ID = parseInt(cfg.variantId, 10);
  var THRESHOLD = parseInt(cfg.threshold, 10) || 10000; // in pence
  var AWAY = cfg.awayText || "You're {amount} away from a FREE gift";
  var UNLOCKED = cfg.unlockedText || 'Your free gift is unlocked!';
  var DEBOUNCE_MS = 350;

  // Sections we ask Shopify to re-render after we mutate the cart, so the
  // drawer, the /cart page and the header bubble all stay in sync.
  var SECTIONS = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];

  var busy = false;
  var pending = false;
  var timer = null;

  /* ---------- helpers ------------------------------------------------------ */

  function money(pence) {
    var s = (pence / 100).toFixed(2);
    return '£' + s.replace(/\.00$/, ''); // £37.50 stays, £40.00 → £40
  }

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  function getCart() {
    return fetch(cartUrl() + '.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function findGift(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].variant_id === GIFT_ID) return items[i];
    }
    return null;
  }

  function parseHTML(str) {
    return new DOMParser().parseFromString(str, 'text/html');
  }

  /* ---------- progress bar (JS owns the live values) ----------------------- */

  function updateBar(cart) {
    var bars = document.querySelectorAll('[data-gift-progress]');
    if (!bars.length) return;
    var subtotal = cart.items_subtotal_price || 0;
    var remaining = Math.max(0, THRESHOLD - subtotal);
    var pct = THRESHOLD > 0 ? Math.min(100, Math.round((subtotal / THRESHOLD) * 100)) : 0;
    var isEmpty = (cart.item_count || 0) === 0;

    for (var i = 0; i < bars.length; i++) {
      var el = bars[i];
      var fill = el.querySelector('[data-gift-progress-fill]');
      var msg = el.querySelector('[data-gift-progress-msg]');
      el.style.display = isEmpty ? 'none' : ''; // bar lives outside the re-rendered table on /cart
      if (fill) fill.style.setProperty('--fill', pct + '%');
      el.setAttribute('data-gift-unlocked', remaining === 0 ? 'true' : 'false');
      if (msg) {
        msg.innerHTML =
          remaining === 0 ? UNLOCKED : AWAY.replace('{amount}', '<strong>' + money(remaining) + '</strong>');
      }
    }
  }

  /* ---------- mirror Dawn's section re-render ------------------------------ */

  function applySections(sections) {
    if (!sections) return;

    if (sections['cart-drawer']) {
      var d = parseHTML(sections['cart-drawer']);
      ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (sel) {
        var target = document.querySelector(sel);
        var source = d.querySelector(sel);
        if (target && source) target.replaceWith(source);
      });
    }

    ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
      if (!sections[id]) return;
      var host = document.getElementById(id);
      if (!host) return;
      var src = parseHTML(sections[id]).querySelector('.js-contents');
      var dst = host.querySelector('.js-contents');
      if (src && dst) dst.innerHTML = src.innerHTML;
    });

    if (sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        var parsed = parseHTML(sections['cart-icon-bubble']);
        var inner = parsed.querySelector('.shopify-section') || parsed.body.firstElementChild;
        if (inner) bubble.innerHTML = inner.innerHTML;
      }
    }
  }

  /* ---------- cart mutations ----------------------------------------------- */

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (data) {
        return { ok: r.ok, data: data };
      });
    });
  }

  function addGift() {
    return postJSON(window.routes.cart_add_url, {
      items: [{ id: GIFT_ID, quantity: 1, properties: { _free_gift: 'true' } }],
      sections: SECTIONS,
      sections_url: window.location.pathname,
    }).then(function (res) {
      if (!res.ok) {
        console.warn('[free-gift] could not add gift (is the product published to Online Store & in stock?)', res.data);
        return;
      }
      applySections(res.data.sections);
    });
  }

  function changeGift(quantity) {
    return postJSON(window.routes.cart_change_url, {
      id: String(GIFT_ID),
      quantity: quantity,
      sections: SECTIONS,
      sections_url: window.location.pathname,
    }).then(function (res) {
      if (res.ok) applySections(res.data.sections);
    });
  }

  /* ---------- reconcile: the single source of truth ------------------------ */

  function reconcile() {
    if (busy) {
      pending = true; // a change arrived mid-flight — run again when we're done
      return;
    }
    busy = true;
    return getCart()
      .then(function (cart) {
        updateBar(cart);
        var qualifies = (cart.items_subtotal_price || 0) >= THRESHOLD;
        var gift = findGift(cart);
        if (qualifies && !gift) return addGift();
        if (qualifies && gift && gift.quantity !== 1) return changeGift(1); // lock to 1
        if (!qualifies && gift) return changeGift(0); // dropped below → remove
      })
      .catch(function (e) {
        console.warn('[free-gift]', e);
      })
      .finally(function () {
        busy = false;
        if (pending) {
          pending = false;
          setTimeout(reconcile, 0);
        }
      });
  }

  function scheduleReconcile() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, DEBOUNCE_MS);
  }

  /* ---------- boot --------------------------------------------------------- */

  function start() {
    // Inside the theme editor: paint the bar but never mutate the merchant's cart.
    if (window.Shopify && window.Shopify.designMode) {
      getCart().then(updateBar).catch(function () {});
      return;
    }
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleReconcile);
    }
    reconcile(); // initial pass on page load
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
